import { isDbClosedError } from "./is-db-closed-error"
import { hydratePreOrderRows, type OnDemandDeps } from "./on-demand-hydrate"

export type { OnDemandDeps } from "./on-demand-hydrate"

/**
 * Below-floor ON-DEMAND fetch (STK-1323).
 *
 * When the recap opens a date older than the kept window, the pre-orders +
 * flows for that slice aren't on device. This fetches them from the dedicated
 * sync-shape endpoint (`GET /pre-orders/on-demand`, STK-1323 backend) and
 * upserts them straight into the Dexie `preOrders` + `preOrderFlows` tables so
 * the existing recap readers pick them up reactively. Fetched rows are marked
 * in `_ephemeral` (TTL/LRU) — they live below the kept floor and are NOT part
 * of the offline set, so they're evictable and never touch `_syncMeta`.
 *
 * Invariants:
 *  - Offline or fetch failure → `{ offlineMiss: true }`, ZERO writes, no
 *    `_syncMeta` mutation, no resync — the recap shows "connect to load older
 *    orders" instead of an empty list.
 *  - Respects the change-queue shield: a row with a pending/syncing local edit
 *    is never clobbered by the server copy.
 *
 * Hydration (shield + writes + `_ephemeral` stamping) is the shared path in
 * `on-demand-hydrate.ts`, same as the by-id fetch (STK-1972) — but NON-atomic
 * (`atomic: false`): a window can carry hundreds of orders, and one big `rw`
 * transaction would hold `_changeQueue` (every user edit) and the pre-order
 * tables (every SSE write) for the whole bulk write. Original shipped
 * behavior, deliberately preserved; rationale in `on-demand-hydrate.ts`.
 */

const ON_DEMAND_URL = "/api/v2/orders/pre-orders/on-demand"

/**
 * A past-date window to hydrate, on the axis the recap is filtered by. The two
 * axes carry DIFFERENT bounds so the server query matches the recap's own Dexie
 * frame (see the backend `findByDateRangeForViewer`):
 *  - `delivery` → `from`/`to` calendar dates (YYYY-MM-DD), tz-less DATE compare.
 *  - `order` → `fromTs`/`toTs` absolute-UTC datetimes (the recap's company-tz
 *    day bounds); raw dates would mis-bucket rows near midnight off-UTC.
 */
export type OnDemandWindow =
  | { axis: "delivery"; from: string; to: string; limit?: number }
  | { axis: "order"; fromTs: string; toTs: string; limit?: number }

export interface OnDemandResult {
  /** Number of rows (pre-orders + flows) written locally. */
  hydrated: number
  /** True when the slice couldn't be fetched (offline / network error). */
  offlineMiss: boolean
}

interface OnDemandResponse {
  preOrders: Array<{ id: string }>
  flows: Array<{ id: string }>
}

/**
 * Fetch a past-date window (on the axis the recap is filtered by) and hydrate
 * the local tables. Returns `{ offlineMiss: true }` — with no side effects —
 * when offline or the request fails.
 */
export async function fetchOnDemandWindow(
  window: OnDemandWindow,
  deps: OnDemandDeps
): Promise<OnDemandResult> {
  if (!deps.isOnline()) return { hydrated: 0, offlineMiss: true }

  let response: OnDemandResponse
  try {
    const params: Record<string, string> =
      window.axis === "delivery"
        ? { axis: "delivery", from: window.from, to: window.to }
        : { axis: "order", fromTs: window.fromTs, toTs: window.toTs }
    if (window.limit !== undefined) params.limit = String(window.limit)
    response = await deps.httpClient.get<OnDemandResponse>(
      ON_DEMAND_URL,
      params
    )
  } catch {
    // No cursor touch, no resync — a clean miss the UI can surface.
    return { hydrated: 0, offlineMiss: true }
  }

  try {
    const { pos, flows } = await hydratePreOrderRows(response, deps, {
      atomic: false,
    })
    return { hydrated: pos.length + flows.length, offlineMiss: false }
  } catch (err) {
    // The store closed mid-write (cross-tab logout/delete). This is a
    // read-through hydration, not user data — surface a clean miss the UI can
    // retry rather than an unhandled `DatabaseClosedError`. The `onClose`
    // watcher reopens + restarts the engine; the next open re-fetches.
    if (isDbClosedError(err)) return { hydrated: 0, offlineMiss: true }
    throw err
  }
}
